// The boss contract (sim side). Every boss is one file in sim/bosses/ exporting a BossDef, plus
// one line in sim/bosses/index.ts. See sim/bosses/README.md for how to add one.
//
// A boss is host-authoritative like every robot: its brain runs only in the Game on the server,
// the bodies it fights with are ordinary Enemy entries (so grenades, rockets, melee, specials,
// prime/detonate, burn and lag compensation all reach them for free), and whatever extra state
// clients need to draw it rides the snapshot's run block through the boss's own enc/dec.

import type { Reader, Writer } from '../codec';
import type { EType } from '../defs';
import type { Enemy } from '../enemies';
import type { Game } from '../game';

/** Which boss. On the wire (run block), so append only; 0 = none. */
export const enum BossKind { None = 0, Cerberus = 1, BulwarkCube = 2, BlackCourt = 3, Angler = 4, Leviathan = 5 }

/** Ev.Boss kinds every boss shares ([eid, kind, a, b]). Each boss numbers its own beats from 16 up
 *  (Cerberus predates this and uses 2..7, see sim/boss.ts BossEv). */
export const enum BossBeat { Intro = 0, Attack = 1, Dead = 8 }

/** The common head of every boss's server state (g.boss). */
export interface BossBase {
  kind: BossKind;
  /** the boss's body enemy, if it has exactly one (Cerberus); 0 when its body is many units */
  eid: number;
}

/** The common head of every boss's run-block payload (RunSnap.boss). Each boss extends it. */
export interface BossNet {
  kind: BossKind;
  eid: number;
  /** current major action (attack / formation id); 0 none */
  atk: number;
  /** boss-defined phase bits */
  phase: number;
}

/** A shot that found the boss. `e` is the unit the damage lands on (rewound like any robot). */
export interface BossHit {
  t: number;
  e: Enemy | null;
  /** armor value of the part (0..4) and its damage multiplier */
  av: number; mul: number;
  /** counts as a head shot for the gun's feedback (the boss's own mul replaces the head bonus) */
  head: boolean;
  /** a gold weak point (prints as a crit) */
  weak: boolean;
  /** boss-defined part id and index (head number, unit slot...) for tookHit */
  part: number; idx: number;
}
export const newBossHit = (): BossHit => ({ t: -1, e: null, av: 0, mul: 1, head: false, weak: false, part: 0, idx: -1 });

export interface Ray { ox: number; oy: number; oz: number; dx: number; dy: number; dz: number }

export interface BossDef<S extends BossBase = BossBase> {
  kind: BossKind;
  /** the name G.U.L.P. prints (notices) */
  name: string;
  /** the RECALL NOTICE line when it drops in */
  recall: string;
  /** enemy types this boss owns (all must be isBossType, i.e. >= 8): the robot loops skip them */
  types: readonly EType[];
  /** its bodies are robots: gun elements proc on them like on any robot (Cerberus: no) */
  procs?: boolean;
  /** Put it on the arena centre (x, z), landing to face the posse at (lookX, lookZ). Spawn its
   *  bodies with g.spawnEnemy; scale hp by `players` and `tierMul` (Cerberus: × (1 + 0.35 (n-1)) × tier). */
  spawn(g: Game, x: number, z: number, lookX: number, lookZ: number, players: number, tierMul: number): S;
  /** Called for each owned enemy in enemy-list order (keeps robot rng order stable). Optional. */
  stepEnemy?(g: Game, s: S, e: Enemy): void;
  /** Called once per tick after the enemies stepped. Optional. */
  step?(g: Game, s: S): void;
  /** Server ray against the boss at the shooter's view tick (rewind with historyAt). t = -1: miss. */
  raycast(g: Game, s: S, r: Ray, view: number, maxT: number, out: BossHit): BossHit;
  /** A shot landed (after armor). `dealt` is the hp it took. */
  tookHit(g: Game, s: S, hit: BossHit, dealt: number): void;
  /** An elemental proc landed on one of its bodies (sim/arsenal.ts proc → game.procd). Optional. */
  proc?(g: Game, s: S, e: Enemy, elem: number, owner: number): void;
  /** An owned enemy died. Return true when that beats the boss. */
  died(g: Game, s: S, e: Enemy): boolean;
  /** The notice line for one of its Ev.Boss beats ('' / undefined = none). */
  note?(s: S, kind: number, a: number, b: number): string | undefined;
  /** Run-block payload (after the kind byte); dec must read exactly what enc wrote. */
  enc(w: Writer, g: Game, s: S): void;
  dec(r: Reader, kind: BossKind): BossNet;
}
